/** @file Draws the dopesheet on a Canvas2D: rows, end shade, loop range, keys, ruler, playhead, marquee. */
import { timelineKeysInBox } from '@pose2d/editor-geometry/timelineKeysInBox';
import { timelineRowRange } from '@pose2d/editor-geometry/timelineRowRange';
import { timelineScale } from '@pose2d/editor-geometry/timelineScale';
import type { ScreenRect, TimelineLayout, TimelineScale } from '@pose2d/editor-geometry/types';
import { dopeKeys } from '../model/dopeKeys';
import { DOPE_PX } from './dopeMetrics';
import type { DopePalette } from './dopePalette';
import { rulerStep } from './rulerStep';

/** The part of CanvasRenderingContext2D the dopesheet uses. */
export type Dope2D = Pick<
  CanvasRenderingContext2D,
  'fillStyle' | 'strokeStyle' | 'lineWidth' | 'font' | 'textBaseline' | 'fillRect' | 'strokeRect' | 'beginPath' | 'moveTo' | 'lineTo' | 'closePath' | 'fill' | 'stroke' | 'fillText' | 'save' | 'restore' | 'rect' | 'clip'
>;

/** Everything one frame of the dopesheet shows; sizes in CSS px, canvas y 0 = the top of the ruler. */
export interface DopeView {
  layout: TimelineLayout;
  scale: TimelineScale;
  width: number;
  height: number;
  /** Content px of the rows scrolled above the rows area. */
  scrollY: number;
  /** Playhead frame (fractional while playing). */
  frame: number;
  /** Animation length in frames (the area after it is shaded). */
  end: number;
  loop: readonly [number, number] | null;
  /** dopeKeys.marks of the selected keys. */
  marks: ReadonlySet<string>;
  /** Frames the selected keys are being dragged by (0 when no drag). */
  drag: number;
  /** Marquee in canvas px, or null. */
  box: ScreenRect | null;
}

const x = (v: DopeView, frame: number): number => timelineScale.frameToX(v.scale, frame);

function diamond(ctx: Dope2D, cx: number, cy: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(cx, cy - r);
  ctx.lineTo(cx + r, cy);
  ctx.lineTo(cx, cy + r);
  ctx.lineTo(cx - r, cy);
  ctx.closePath();
  ctx.fill();
}

/** Group rows raised, the frames after the end shaded, the loop range tinted. */
function drawRows(ctx: Dope2D, v: DopeView, p: DopePalette): void {
  const [first, end] = timelineRowRange(v.layout, v.scrollY, v.scrollY + v.height - DOPE_PX.ruler);
  ctx.fillStyle = p.raised;
  for (let i = first; i < end; i++) {
    const row = v.layout.rows[i];
    if (row?.kind === 'group') ctx.fillRect(0, DOPE_PX.ruler + row.y - v.scrollY, v.width, v.layout.rowHeight);
  }
  ctx.fillStyle = p.shade;
  ctx.fillRect(Math.max(0, x(v, v.end)), DOPE_PX.ruler, v.width, v.height);
  if (v.loop === null) return;
  ctx.fillStyle = p.accentSoft;
  ctx.fillRect(x(v, v.loop[0]), DOPE_PX.ruler, x(v, v.loop[1]) - x(v, v.loop[0]), v.height);
}

/** Visible keys; selected keys in the accent colour, shifted by the drag. */
function drawKeys(ctx: Dope2D, v: DopeView, p: DopePalette): void {
  const pad = DOPE_PX.groupKey + Math.abs(v.drag) * v.scale.pxPerFrame;
  const box = { x0: -pad, y0: v.scrollY, x1: v.width + pad, y1: v.scrollY + v.height - DOPE_PX.ruler };
  for (const hit of timelineKeysInBox(v.layout, v.scale, box)) {
    const row = v.layout.rows[hit.row];
    if (row === undefined) continue;
    const selected = v.marks.has(dopeKeys.mark(row.target, row.channel, hit.frame));
    ctx.fillStyle = selected ? p.accent : row.kind === 'group' ? p.muted : p.fg;
    const cy = DOPE_PX.ruler + row.y - v.scrollY + v.layout.rowHeight / 2;
    diamond(ctx, x(v, selected ? hit.frame + v.drag : hit.frame), cy, row.kind === 'group' ? DOPE_PX.groupKey : DOPE_PX.key);
  }
}

/** Ruler band with frame ticks, labels every rulerStep frames and the loop range bar. */
function drawRuler(ctx: Dope2D, v: DopeView, p: DopePalette): void {
  ctx.fillStyle = p.raised;
  ctx.fillRect(0, 0, v.width, DOPE_PX.ruler);
  const step = rulerStep(v.scale.pxPerFrame, DOPE_PX.label);
  const [first, last] = timelineScale.visibleFrames(v.scale, v.width);
  ctx.strokeStyle = p.line;
  ctx.fillStyle = p.muted;
  ctx.beginPath();
  for (let f = Math.max(0, Math.ceil(first / step) * step); f <= last; f += step) {
    ctx.moveTo(x(v, f), DOPE_PX.ruler - 6);
    ctx.lineTo(x(v, f), DOPE_PX.ruler);
    ctx.fillText(String(f), x(v, f) + 2, DOPE_PX.ruler / 2);
  }
  ctx.stroke();
  if (v.loop === null) return;
  ctx.fillStyle = p.accent;
  ctx.fillRect(x(v, v.loop[0]), DOPE_PX.ruler - 3, x(v, v.loop[1]) - x(v, v.loop[0]), 3);
}

/** Playhead line over everything, with its frame (one decimal while between frames) in the ruler. */
function drawPlayhead(ctx: Dope2D, v: DopeView, p: DopePalette): void {
  const px = x(v, v.frame);
  ctx.strokeStyle = p.playhead;
  ctx.beginPath();
  ctx.moveTo(px, 0);
  ctx.lineTo(px, v.height);
  ctx.stroke();
  ctx.fillStyle = p.playhead;
  ctx.fillRect(px - 16, 0, 32, DOPE_PX.ruler - 6);
  ctx.fillStyle = p.bg;
  ctx.fillText(Number.isInteger(v.frame) ? String(v.frame) : v.frame.toFixed(1), px - 14, DOPE_PX.ruler / 2 - 3);
}

/** Draws one frame; the caller has set the device-pixel transform (CSS px coordinates here). */
export function drawDopesheet(ctx: Dope2D, v: DopeView, p: DopePalette): void {
  ctx.fillStyle = p.bg;
  ctx.fillRect(0, 0, v.width, v.height);
  ctx.font = p.font;
  ctx.textBaseline = 'middle';
  ctx.lineWidth = 1;
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, DOPE_PX.ruler, v.width, v.height - DOPE_PX.ruler);
  ctx.clip();
  drawRows(ctx, v, p);
  drawKeys(ctx, v, p);
  ctx.restore();
  drawRuler(ctx, v, p);
  drawPlayhead(ctx, v, p);
  if (v.box === null) return;
  ctx.fillStyle = p.accentSoft;
  ctx.strokeStyle = p.accent;
  ctx.fillRect(v.box.x0, v.box.y0, v.box.x1 - v.box.x0, v.box.y1 - v.box.y0);
  ctx.strokeRect(v.box.x0, v.box.y0, v.box.x1 - v.box.x0, v.box.y1 - v.box.y0);
}
